import { useEffect, useRef, useState } from 'react';
import { Check, Loader2, AlertTriangle } from 'lucide-react';

import { useAdmin } from '@lok/context/AdminContext';

// How long the "Saved" confirmation lingers after a save before it fades back
// to nothing — the Google-Docs pattern the client asked for (CON-190 follow-up).
const RECENT_SAVED_MS = 3000;

// Per-phase presentation for the compact status row shown inside the Admin
// Controls panel (CON-190).
const PHASES = {
  saving: { label: 'Saving…', cls: 'text-gray-500 dark:text-gray-400', Icon: Loader2, spin: true },
  saved: { label: 'Saved', cls: 'text-primary-green', Icon: Check, spin: false },
  error: { label: "Couldn't save", cls: 'text-red-500', Icon: AlertTriangle, spin: false }
};

// Autosave status row for the Admin Controls panel. Change-driven, not
// view-driven: nothing renders just because you're in admin view — it only
// appears in response to an actual save, Google-Docs style:
//   saving            → spinner + "Saving…"
//   just saved (≤3s)  → check + "Saved", then it disappears
//   idle / not saving → hidden
const SaveStatusIndicator = () => {
  const { isAdminView, saveStatus, lastSavedAt } = useAdmin();
  const [isRecent, setIsRecent] = useState(false);
  // Seeded so a pre-existing save (e.g. from earlier this session) doesn't
  // flash "saved" on mount — only a NEW save shows.
  const prevSavedAtRef = useRef(lastSavedAt);

  useEffect(() => {
    if (lastSavedAt === prevSavedAtRef.current) return undefined;
    prevSavedAtRef.current = lastSavedAt;
    setIsRecent(true);
    const timer = setTimeout(() => setIsRecent(false), RECENT_SAVED_MS);
    return () => clearTimeout(timer);
  }, [lastSavedAt]);

  let phaseKey = null;
  if (saveStatus === 'saving') phaseKey = 'saving';
  else if (saveStatus === 'error') phaseKey = 'error';
  else if (saveStatus === 'saved' && isRecent) phaseKey = 'saved';

  if (!isAdminView || !phaseKey) return null;

  const { label, cls, Icon, spin } = PHASES[phaseKey];

  return (
    <div
      role='status'
      aria-live='polite'
      className={`flex items-center justify-end gap-1.5 px-1 text-xs font-medium ${cls}`}
    >
      <Icon
        className={`h-3.5 w-3.5 shrink-0 ${spin ? 'animate-spin' : ''}`}
        aria-hidden='true'
      />
      <span>{label}</span>
    </div>
  );
};

export default SaveStatusIndicator;
